import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { translate, TranslateResult } from '../services/translate';

type Language = 'zh' | 'en' | 'jp';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (text: string) => string;
  translating: boolean;
}

const I18nContext = createContext<I18nContextType>({} as I18nContextType);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [translating, setTranslating] = useState(false);

  // 缓存已翻译内容
  const getCachedTranslation = useCallback(
    (text: string) => translations[text] || text,
    [translations]
  );

  // 自动翻译页面内容
  useEffect(() => {
    const translatePageContent = async () => {
      if (language === 'en') return;

      setTranslating(true);
      try {
        // 获取页面所有需要翻译的文本
        const texts = Array.from(
          new Set(
            Array.from(document.querySelectorAll('[data-translate]'))
              .map(el => el.textContent?.trim())
              .filter(Boolean)
          )
        ) as string[];

        // 批量翻译（百度API支持批量，每批最多2000字符）
        const batchSize = 10;
        for (let i = 0; i < texts.length; i += batchSize) {
          const batch = texts.slice(i, i + batchSize);
          const result = await translate(batch.join('\n'), language);
          
          setTranslations(prev => ({
            ...prev,
            ...Object.fromEntries(
              result.map((item, index) => [batch[index], item.dst])
            )
          }));
        }
      } finally {
        setTranslating(false);
      }
    };

    translatePageContent();
  }, [language]);
    
// useEffect(() => {
//   const translatePageContent = async () => {
//     if (language === 'zh') return;

//     setTranslating(true);
//     try {
//       // 添加DOM查询容错处理
//       const elements = Array.from(document.querySelectorAll('[data-translate]'));
//       const texts = elements
//         .map(el => el.textContent?.trim())
//         .filter(text => text && text.length > 0);

//       // 添加空值检查
//       if (texts.length === 0) return;

//       // 分块处理（百度API单次最多2000字符）
//       const CHUNK_SIZE = 10;
//       const BATCH_SIZE = 2000;
      
//       for (let i = 0; i < texts.length; i += CHUNK_SIZE) {
//         const chunk = texts.slice(i, i + CHUNK_SIZE);
//         let currentBatch = '';
//         const batchToTranslate: string[] = [];

//         // 分批次处理
//         for (const text of chunk) {
//           if ((currentBatch + text).length > BATCH_SIZE) {
//             batchToTranslate.push(currentBatch);
//             currentBatch = '';
//           }
//           currentBatch += text + '\n';
//         }
//         if (currentBatch) batchToTranslate.push(currentBatch);

//         // 执行翻译
//         for (const batch of batchToTranslate) {
//           const result = await translate(batch, language);
//           // 处理结果映射...
//         }
//       }
//     } catch (error:any) {
//       console.error('Translation failed:', error);
//       // 添加用户提示
//       alert(`翻译失败: ${error.message}`);
//     } finally {
//       setTranslating(false);
//     }
//   };

//   translatePageContent();
// }, [language]);

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage,
        t: getCachedTranslation,
        translating,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);